import React from 'react';
import styled from 'styled-components';
import { StickToBottom } from 'use-stick-to-bottom';
import { UserMessage } from '@/features/ai-agents/components/user-message';
import { AiChatMessage } from '@/features/ai-agents/shared/models';
import { useChatSession } from '@/features/ai-agents/chat/context';
import { AgentMessage } from '@/features/ai-agents/components/agent-message';
import { VSpace } from '@/components/spacing';
import { breakpoints } from '@/components/responsive';
import { ScrollToButtons } from '@/features/ai-agents/chat/scroll-buttons';

export interface ChatMessagesProps {
  showReasoning?: boolean;
  showTools?: boolean;

  isLocalHost?: boolean;

  onScrollStateChange?: (isAtBottom: boolean, isAtTop: boolean) => void;
}

export function ChatMessages({
  showTools,
  showReasoning,
  onScrollStateChange,
}: ChatMessagesProps) {
  const { messages, processedEvents, isProcessing } = useChatSession();

  return (
    <Wrapper>
      <StyledStickToBottom resize="smooth" initial="smooth">
        {/* @ts-ignore */}
        <StickToBottom.Content className="content">
          <MessagesWrapper>
            <VSpace value={0.1} />
            {messages?.value?.map((msg, index) => {
              const isLatestAndProcessing =
                isProcessing.value && index === messages.value.length - 1;
              return msg.role === `user` ? (
                <UserMessage key={messageKey(msg, index)} message={msg} />
              ) : (
                <AgentMessage
                  key={messageKey(msg, index)}
                  message={msg}
                  showReasoning={showReasoning}
                  showTools={showTools}
                  isProcessing={isProcessing}
                  processedEvents={
                    isLatestAndProcessing ? processedEvents : undefined
                  }
                />
              );
            })}
            <VSpace value={14} />
          </MessagesWrapper>
        </StickToBottom.Content>
        <ScrollToButtons onChange={onScrollStateChange} />
      </StyledStickToBottom>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
`;

const StyledStickToBottom = styled(StickToBottom)`
  && {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;

    height: 34vh;

    ${breakpoints.upH(400)} {
      height: 41vh;
    }

    ${breakpoints.upH(600)} {
      height: 46vh;
    }

    ${breakpoints.upH(650)} {
      height: 51vh;
    }

    ${breakpoints.upH(700)} {
      height: 57vh;
    }

    ${breakpoints.upH(750)} {
      height: 63vh;
    }

    ${breakpoints.upH(800)} {
      height: 65vh;
    }

    ${breakpoints.upH(850)} {
      height: 66vh;
    }

    ${breakpoints.upH(900)} {
      height: 70vh;
    }

    ${breakpoints.upH(950)} {
      height: 72vh;
    }

    ${breakpoints.upH(1000)} {
      height: 74vh;
    }

    ${breakpoints.upH(1050)} {
      height: 76vh;
    }

    ${breakpoints.upH(1100)} {
      height: 78vh;
    }

    ${breakpoints.upH(1150)} {
      height: 79vh;
    }

    ${breakpoints.upH(1200)} {
      height: 80vh;
    }

    ${breakpoints.upH(1250)} {
      height: 82vh;
    }

    ${breakpoints.upH(1300)} {
      height: 83vh;
    }

    ${breakpoints.upH(1350)} {
      height: 84vh;
    }

    ${breakpoints.upH(1400)} {
      height: 86vh;
    }

    mask-image: linear-gradient(
      var(--mask-direction, to bottom),
      hsl(0 0% 0% / 0),
      rgb(248 249 251 / 1) 5%,
      rgb(248 249 251 / 1) 85%,
      hsl(0 0% 0% / 0)
    );

    .content {
    }
  }
`;

export function messageKey(message: AiChatMessage, index: number): string {
  return `${message.role}-${message.created_at}-${index}`;
}

const MessagesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
  width: 100%;
  height: auto;
`;
